import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  timePunchesService,
  payRatesService,
  timesheetsService,
  ClockInDto,
  CreatePunchDto,
  ApproveExceptionDto,
  RejectExceptionDto,
  CreatePayRateDto,
  GenerateTimesheetDto,
  RejectTimesheetDto,
  TimesheetStatus,
} from '../services/time-punches.service';

// Time Punches Hooks
export function useCurrentStatus() {
  return useQuery({
    queryKey: ['time-punches', 'current-status'],
    queryFn: timePunchesService.getCurrentStatus,
    refetchInterval: 30000, // Refetch every 30 seconds
  });
}

export function useTodaysPunches(date?: string) {
  return useQuery({
    queryKey: ['time-punches', 'today', date],
    queryFn: () => timePunchesService.getTodaysPunches(date),
    refetchInterval: 30000, // Refetch every 30 seconds
  });
}

export function usePunchesByOrganization(startDate: string, endDate: string) {
  return useQuery({
    queryKey: ['time-punches', 'organization', startDate, endDate],
    queryFn: () => timePunchesService.getPunchesByOrganization(startDate, endDate),
    enabled: !!startDate && !!endDate,
  });
}

export function usePunchesByAgent(agentId: string, startDate: string, endDate: string) {
  return useQuery({
    queryKey: ['time-punches', 'agent', agentId, startDate, endDate],
    queryFn: () => timePunchesService.getPunchesByAgent(agentId, startDate, endDate),
    enabled: !!agentId && !!startDate && !!endDate,
  });
}

export function usePendingExceptions() {
  return useQuery({
    queryKey: ['time-punches', 'exceptions'],
    queryFn: timePunchesService.getPendingExceptions,
    refetchInterval: 60000, // Refetch every minute
  });
}

export function useClockIn() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: ClockInDto) => timePunchesService.clockIn(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time-punches'] });
    },
  });
}

export function useClockOut() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: ClockInDto) => timePunchesService.clockOut(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time-punches'] });
    },
  });
}

export function useStartBreak() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: timePunchesService.startBreak,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time-punches'] });
    },
  });
}

export function useEndBreak() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: timePunchesService.endBreak,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time-punches'] });
    },
  });
}

export function useStartLunch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: timePunchesService.startLunch,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time-punches'] });
    },
  });
}

export function useEndLunch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: timePunchesService.endLunch,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time-punches'] });
    },
  });
}

export function useCreateManualPunch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreatePunchDto) => timePunchesService.createManualPunch(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time-punches'] });
    },
  });
}

export function useUpdatePunch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: any }) =>
      timePunchesService.updatePunch(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time-punches'] });
    },
  });
}

export function useDeletePunch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => timePunchesService.deletePunch(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time-punches'] });
    },
  });
}

export function useApproveException() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: ApproveExceptionDto }) =>
      timePunchesService.approveException(id, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time-punches'] });
    },
  });
}

export function useRejectException() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: RejectExceptionDto }) =>
      timePunchesService.rejectException(id, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time-punches'] });
    },
  });
}

// Pay Rates Hooks
export function usePayRates() {
  return useQuery({
    queryKey: ['pay-rates'],
    queryFn: payRatesService.getAllPayRates,
  });
}

export function useAgentsWithPayRates() {
  return useQuery({
    queryKey: ['pay-rates', 'agents-all'],
    queryFn: payRatesService.getAllAgentsWithPayRates,
  });
}

export function useCurrentPayRate(agentId: string) {
  return useQuery({
    queryKey: ['pay-rates', agentId, 'current'],
    queryFn: () => payRatesService.getCurrentRate(agentId),
    enabled: !!agentId,
  });
}

export function usePayRateHistory(agentId: string) {
  return useQuery({
    queryKey: ['pay-rates', agentId, 'history'],
    queryFn: () => payRatesService.getHistory(agentId),
    enabled: !!agentId,
  });
}

export function useCreatePayRate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreatePayRateDto) => payRatesService.createPayRate(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pay-rates'] });
    },
  });
}

export function useUpdatePayRate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<CreatePayRateDto> }) =>
      payRatesService.updatePayRate(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pay-rates'] });
    },
  });
}

export function useDeletePayRate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => payRatesService.deletePayRate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pay-rates'] });
    },
  });
}

export function useBulkUpdatePayRates() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (updates: any[]) => payRatesService.bulkUpdatePayRates(updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pay-rates'] });
    },
  });
}

// Timesheets Hooks
export function useTimesheets(status?: TimesheetStatus) {
  return useQuery({
    queryKey: ['timesheets', status],
    queryFn: () => timesheetsService.getAllTimesheets(status),
  });
}

export function useTimesheetsByAgent(agentId: string) {
  return useQuery({
    queryKey: ['timesheets', 'agent', agentId],
    queryFn: () => timesheetsService.getTimesheetsByAgent(agentId),
    enabled: !!agentId,
  });
}

export function useGenerateTimesheet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: GenerateTimesheetDto) => timesheetsService.generateTimesheet(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timesheets'] });
    },
  });
}

export function useBulkGenerateTimesheets() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ periodStart, periodEnd }: { periodStart: string; periodEnd: string }) =>
      timesheetsService.bulkGenerateTimesheets(periodStart, periodEnd),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timesheets'] });
    },
  });
}

export function useApproveTimesheet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => timesheetsService.approveTimesheet(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timesheets'] });
    },
  });
}

export function useRejectTimesheet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: RejectTimesheetDto }) =>
      timesheetsService.rejectTimesheet(id, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timesheets'] });
    },
  });
}

export function useDeleteTimesheet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => timesheetsService.deleteTimesheet(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timesheets'] });
    },
  });
}
